import * as React from "react";

import { getProjectLandingPath } from "@v2/features/account/postLoginLanding";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { userRouter } from "@v2/trpc/routes/user";
import { routes } from "@v2/utils/router";
import { generatePath, Navigate, Outlet, useLocation } from "react-router-dom";
import { getLandingUserQueryInput } from "replo-utils/lib/lastVisitedDashboardIds";

// Mapping from old routes to new shops routes
const ROUTE_MAPPINGS: Record<string, string> = {
  // Workspace analytics routes
  "/workspace/:workspaceId/analytics": "/shops/:projectId/analytics",
  [routes.analytics.home]: routes.shops.analytics.home,
  [routes.analytics.overview]: routes.shops.analytics.overview,
  [routes.analytics.pageDetails]: routes.shops.analytics.pageDetails,
  [routes.analytics.insights]: routes.shops.analytics.insights,

  // Workspace experiments routes
  [routes.workspace.experiments.list]: routes.shops.experiments.list,
  [routes.workspace.experiments.details]: routes.shops.experiments.details,
  [routes.workspace.experiments.results]: routes.shops.experiments.results,
  [routes.workspace.experiments.settings]:
    routes.shops.settings.shops.abtesting,
  [routes.workspace.experiments.edit]: routes.shops.experiments.edit,

  // Workspace projects -> site
  [routes.workspace.projects]: routes.shops.site,

  // Workspace settings routes
  "/workspace/:workspaceId/members": routes.shops.settings.workspace.members,
  [routes.workspace.billing]: routes.shops.settings.workspace.billing,

  // Workspace integrations
  [routes.workspace.integrations.list]:
    routes.shops.settings.shops.integrations.home,
  [routes.workspace.integrations.shopify]:
    routes.shops.settings.shops.integrations.home,

  // Workspace agnostic routes
  "/analytics": "/shops/:projectId/analytics",
  "/analytics/home": routes.shops.analytics.home,
  "/analytics/overview": routes.shops.analytics.overview,
  "/analytics/deep-dive": routes.shops.analytics.pageDetails,
  "/analytics/insights": routes.shops.analytics.insights,
  "/abtesting": routes.shops.experiments.list,

  // Profile Settings
  "/settings/profile": "/shops/:projectId/settings/profile",
  "/settings/security": "/shops/:projectId/settings/profile",

  // All projects
  "/project": routes.shops.site,
  "/project/new": routes.shops.site,
  [routes.home.shareTemplate]: routes.shareTemplate,
  "/home": routes.shops.site,
};

// Function to match current path against patterns and return the shops route
function getShopsRedirectPath(currentPath: string): string | null {
  for (const [pattern, targetPath] of Object.entries(ROUTE_MAPPINGS)) {
    // Convert pattern to regex for exact matching
    const patternRegex = new RegExp(
      `^${pattern.replace(/:[^/]+/g, "([^/]+)")}$`,
    );

    const match = patternRegex.exec(currentPath);
    if (match) {
      // Extract parameter names from the pattern
      const paramNames =
        pattern.match(/:([^/]+)/g)?.map((item) => item.slice(1)) ?? [];
      const paramValues = match.slice(1);

      // Create a mapping of parameter names to values
      const params: Record<string, string> = {};
      paramNames.forEach((name, index) => {
        const value = paramValues[index];
        if (value !== undefined) {
          params[name] = value;
        }
      });

      // Replace parameters in target pattern
      let result = targetPath;
      Object.entries(params).forEach(([name, value]) => {
        result = result.replace(`:${name}`, value);
      });

      return result;
    }
  }

  return null;
}

// NOTE (Craig 2025-06-11): This will handle redirecting old routes to the new shops UI.
export const ShopsRedirectRoute: React.FC = () => {
  const location = useLocation();
  const { user } = useCurrentUser();
  const shopsPath = getShopsRedirectPath(location.pathname);

  // Fresh last-visited lookup for mapped redirects (REPL-30512).
  const [landingUserQueryInput] = React.useState(getLandingUserQueryInput);
  const { data: landingUser, isLoading: isLoadingLandingUser } =
    userRouter.get.useQuery(landingUserQueryInput, {
      staleTime: 0,
      enabled: shopsPath !== null,
    });

  // Prefer the fresh last-visited lookup; do not fall through to a stale
  // CurrentUserProvider user while that query is still in flight.
  const projectId = landingUser?.defaultProjectId ?? user?.defaultProjectId;
  const resolvedUser = landingUser ?? user;

  if (shopsPath !== null && isLoadingLandingUser) {
    return null;
  }

  if (projectId && shopsPath) {
    // The landing route opens the project on its own dashboard (REPL-30166).
    const projectLandingPath = getProjectLandingPath(resolvedUser);
    if (projectLandingPath !== null && shopsPath === routes.shops.site) {
      return <Navigate to={projectLandingPath} replace />;
    }
    return <Navigate to={generatePath(shopsPath, { projectId })} replace />;
  }

  // If no mapping exists or no project ID, render the regular route.
  // Note (Noah, 2025-09-26): This does mean that the user could get to a blank screen if
  // they have no projects at all, but we'll detect that in App.tsx and show them the new
  // project flow in that case
  return <Outlet />;
};
